import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { PlayCircle, XCircle } from 'react-bootstrap-icons';

/**
 * Reusable modal for playing videos (inline playback, no download).
 * Use for virtual tour videos, model previews, etc.
 */
const VideoPlayerModal = ({ show, onClose, videoUrl, title = 'Video' }) => {
    const videoRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const loadTimeoutRef = useRef(null);

    useEffect(() => {
        if (show && videoUrl) {
            setIsLoading(true);
            setHasError(false);
            loadTimeoutRef.current = setTimeout(() => setIsLoading(false), 8000);
            return () => loadTimeoutRef.current && clearTimeout(loadTimeoutRef.current);
        }
    }, [show, videoUrl]);

    useEffect(() => {
        if (!show && videoRef.current) {
            videoRef.current.pause();
        }
    }, [show]);

    const handleLoaded = () => {
        if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
        setIsLoading(false);
        setHasError(false);
    };

    const handleError = () => {
        if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
        setIsLoading(false);
        setHasError(true);
    };

    const handleClose = () => {
        if (videoRef.current) videoRef.current.pause();
        if (onClose) onClose();
    };

    if (!show) return null;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            size="lg"
            style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
        >
            <Modal.Header className="border-0 py-3 px-4" style={{ backgroundColor: '#1a1a2e', color: '#eee' }}>
                <Modal.Title className="d-flex align-items-center" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    <PlayCircle className="mr-2" size={22} />
                    {title}
                </Modal.Title>
                <button
                    type="button"
                    className="close text-white"
                    onClick={handleClose}
                    aria-label="Close"
                    style={{ opacity: 0.9, fontSize: '1.5rem', padding: '0.25rem', lineHeight: 1 }}
                >
                    <XCircle size={28} />
                </button>
            </Modal.Header>
            <Modal.Body className="p-0" style={{ backgroundColor: '#0f0f1a' }}>
                <div className="position-relative" style={{ minHeight: '420px', backgroundColor: '#000' }}>
                    {videoUrl ? (
                        <>
                            {isLoading && (
                                <div
                                    className="position-absolute d-flex flex-column align-items-center justify-content-center w-100 h-100"
                                    style={{ top: 0, left: 0, backgroundColor: '#0f0f1a', zIndex: 2 }}
                                >
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                    <span className="mt-3 text-muted">Loading video...</span>
                                </div>
                            )}
                            {hasError && (
                                <div
                                    className="position-absolute d-flex flex-column align-items-center justify-content-center w-100 h-100 p-4"
                                    style={{ top: 0, left: 0, backgroundColor: '#0f0f1a', zIndex: 2 }}
                                >
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                                        style={{ width: 64, height: 64, backgroundColor: 'rgba(220, 53, 69, 0.2)' }}
                                    >
                                        <XCircle size={32} className="text-danger" />
                                    </div>
                                    <h6 className="text-white mb-2">Unable to load video</h6>
                                    <p className="text-muted text-center small mb-0">
                                        The video could not be loaded. Please check your connection and try again.
                                    </p>
                                </div>
                            )}
                            <video
                                ref={videoRef}
                                src={videoUrl}
                                controls
                                playsInline
                                className="w-100"
                                style={{ maxHeight: '70vh', display: hasError ? 'none' : 'block' }}
                                onLoadedData={handleLoaded}
                                onCanPlay={handleLoaded}
                                onError={handleError}
                            />
                        </>
                    ) : (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center w-100 h-100 p-4"
                            style={{ minHeight: '420px' }}
                        >
                            <PlayCircle size={48} className="text-muted mb-3" />
                            <p className="text-muted mb-0">No video available</p>
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default VideoPlayerModal;
